import { Component, inject, Input, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ActiveWorkspaceUpdatedEvent,
  Brand,
  CampaignMainFeatureConfig,
  DriveCampaign,
  FeatureTypes,
  Project,
  ProjectImageUpdatedEvent,
  ProjectWorkspaceUpdatedEvent,
  User,
  WorkspaceAvatarUpdatedEvent,
  WorkspaceDeleted,
  WorkspaceDTO,
  WorkspaceFeatureConfig,
} from '../../shared/interfaces';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { AuthService } from '../../shared/services/auth.service';
import { BroadcastService } from '../../shared/services/broadcast.service';
import { CompanyService } from '../../shared/services/company.service';
import { CryptoService } from '../../shared/services/crypto.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { PermissionService } from '../../shared/services/permissions.service';
import { ProjectV2ServiceService } from '../../shared/services/project-v2-service.service';
import { onLoadProject, ProjectService } from '../../shared/services/project.service';
import { ReportingService } from '../../shared/services/reporting.service';
import { TimelineService } from '../../shared/services/timeline.service';
import { UserTourService } from '../../shared/services/user-tour.service';
import { WorkspaceService } from '../../shared/services/workspace.service';
import { SelfTrainerV2Service } from '../../shared/services/self-trainer-v2.service';
import { FeatureConfigService } from '../../shared/services/feature-config.service';
import { BrandService } from '../../shared/services/brand.service';
import { AllowedOperationsUpdatedEvent } from '../../shared/models/events';
import { isCampaignLocked } from '../../shared/utils/permission-checks';
import { environment } from '../../../environments/environment';

type SidebarMenuTypes = 'brand' | 'workspace' | 'campaign' | 'project' | 'account';

@Component({
  selector: 'mtm-left-sidebar-floating',
  templateUrl: './left-sidebar-floating.component.html',
  styleUrl: './left-sidebar-floating.component.scss',
  standalone: false,
})
export class LeftSidebarFloatingComponent implements OnDestroy, OnInit {
  @Input() campaignName: string;
  @Input() isAgencyUser: boolean = false;
  @Input() hasReportMenus = false;
  @Input() hasSelfTrainerMenus: boolean = false;
  private workspaceSubscription: Subscription = new Subscription();
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly broadcastService = inject(BroadcastService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly route = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);
  private readonly projectService = inject(ProjectService);
  private readonly companyService = inject(CompanyService);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly userTourService = inject(UserTourService);
  private readonly cryptoService = inject(CryptoService);
  private readonly timelineService = inject(TimelineService);
  private readonly projectV2Service = inject(ProjectV2ServiceService);
  private readonly reportingService = inject(ReportingService);
  private readonly selfTrainerV2Service = inject(SelfTrainerV2Service);
  private readonly featureConfigService = inject(FeatureConfigService);
  protected readonly brandService = inject(BrandService);
  authUser: User;
  project: Project | null = null;
  activeWorkspace: WorkspaceDTO | null = null;
  activeCampaign: DriveCampaign | null = null;
  inMarketplaceWorkspace = false;
  canEditWorkspace: boolean = false;
  canEditCampaign: boolean = false;
  publicReviewLink = false;
  isSidebarOpen = false;
  cacheBuster: any = new Date().getTime().toString();
  projectCacheBuster = Date.now().toString();
  workspaces: WorkspaceExtraDTO[] = [];

  activeMenuItem: string = '';
  currentWorkspace: WorkspaceDTO | null = null;
  isActiveCampaignMenuHide: boolean = true;
  isActiveWorkspaceMenuHide: boolean = true;
  isAdmin: boolean = false;
  canSignInAdminStudio: boolean = false;
  readonly adminStudioUrl: string = environment.adminStudio?.baseUrl;
  canManageCompanyUsers: boolean = false;
  canManageBrand: boolean = false;
  canOpenProjectSettings: boolean = false;
  menuStates = {
    brand: {
      isExpanded: false,
      toggleAlt: 'expand',
      toggleIcon: '/assets/svg/sidebar/expand-arrow.svg',
    },
    workspace: {
      isExpanded: false,
      toggleAlt: 'expand',
      toggleIcon: '/assets/svg/sidebar/expand-arrow.svg',
    },
    campaign: {
      isExpanded: false,
      toggleAlt: 'expand',
      toggleIcon: '/assets/svg/sidebar/expand-arrow.svg',
    },
    project: {
      isExpanded: false,
      toggleAlt: 'expand',
      toggleIcon: '/assets/svg/sidebar/expand-arrow.svg',
    },
    account: {
      isExpanded: false,
      toggleAlt: 'expand',
      toggleIcon: '/assets/svg/sidebar/expand-arrow.svg',
    },
  };
  protected workspaceConfig: WorkspaceFeatureConfig | null;
  protected campaignMainConfig: CampaignMainFeatureConfig | null;

  private readonly ngUnsubscribe = new Subject<boolean>();

  get isBrandMenuVisible(): boolean {
    return this.brandService.canSwitchBrand() && !this.publicReviewLink;
  }

  get isActiveWorkspaceMenuVisible(): boolean {
    return !!this.activeWorkspace;
  }

  get isActiveCampaignMenuVisible(): boolean {
    return !!this.activeCampaign && !this.project;
  }

  get isActiveProjectMenuVisible() {
    return this.project;
  }

  private readonly isAssetListEnabled = toSignal(
    this.featureConfigService.isFeatureActive$(FeatureTypes.FEA_CAMPAIGN_ASSETLIST),
    { initialValue: false }
  );

  /** Asset list menu is only for campaign projects with the asset-list feature on. */
  get isAssetListMenuItemVisible(): boolean {
    return !!this.project?.campaignId && this.isAssetListEnabled();
  }

  get isLtgMainMenuVisible() {
    return !this.project || this.publicReviewLink;
  }

  get isWorkspaceMenuVisible(): boolean {
    return this.workspaceConfig?.active && !this.isAgencyUser;
  }

  get isCampaignMenuVisible(): boolean {
    return this.campaignMainConfig?.active && !this.isAgencyUser;
  }

  get driveCampaignSettingsQueryParams() {
    return {
      wid: this.cryptoService.encryptString(this.activeCampaign.workspaceId),
      dcId: this.cryptoService.encryptString(this.activeCampaign.id),
    };
  }

  get cacheBusterString(): string {
    return this.cacheBuster.toString();
  }

  ngOnInit() {
    this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      this.publicReviewLink = data.publicReviewLink;
    });
    this.currentWorkspace = this.workspaceService.getActiveWorkspace();
    this.authUser = this.authService.getAuthUser()!;
    this.refreshPermissionFlags();
    this.isAgencyUser = this.authUser?.userPermissionType === 'AGENCY';
    this.publicReviewLink = this.route.snapshot.data.publicReviewLink;
    if (this.currentWorkspace) {
      this.activeWorkspace = this.currentWorkspace;
      this.checkWorkspaceAccess();
    }
    // Idempotent, and self-guarded against public/review links inside the service.
    this.brandService.loadUserBrands();
    this.handleUserEvents();
    this.handleWorkspacesEvents();
    this.handleCampaignEvents();
    this.handleProjectEvents();
    this.detectRouteChange();
    this.trackNavigation(this.router.url);
    // Read persisted state after trackNavigation so it is not cleared by the initial
    // activeCampaign$.next(null) that trackNavigation emits for non-task URLs on reload.
    if (this.timelineService?.driveCampaignData) {
      this.activeCampaign = this.timelineService.driveCampaignData;
    }

    this.timelineService.activeCampaign$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((campaign) => {
      this.activeCampaign = campaign;
    });

    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_WORKSPACE_MAIN, FeatureTypes.FEA_CAMPAIGN_MAIN])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.workspaceConfig = response[FeatureTypes.FEA_WORKSPACE_MAIN];
          this.campaignMainConfig = response[FeatureTypes.FEA_CAMPAIGN_MAIN];
        },
      });
  }

  getActiveWorkspace(companyId: any, workspaceId: any): void {
    if (!workspaceId) {
      return;
    }
    this.workspaceSubscription = this.workspaceService
      .getWorkspaceById(companyId, workspaceId, false, {
        redirectOnForbidden: false,
      })
      .subscribe({
        next: (activeWorkspace) => {
          this.activeWorkspace = activeWorkspace;
          this.timelineService.workspaceName = this.activeWorkspace.name;
        },
        error: (error) => {
          console.log('Error fetching active workspace:', error);
          this.activeWorkspace = null;
        },
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  navigateTo(path: string) {
    this.router.navigateByUrl('/temporaryRedirect', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(path);
    });
  }

  private refreshPermissionFlags(): void {
    this.isAdmin = this.permissionService.isPlatformAdmin();
    this.canSignInAdminStudio = this.permissionService.canSignInAdminStudio();
    this.canManageCompanyUsers = this.permissionService.canManageCompanyUsers();
    this.canManageBrand = this.permissionService.canManageBrand();
    this.canOpenProjectSettings =
      this.permissionService.canUpdateProject() || this.permissionService.isPlatformAdminOrAccountManager();
    this.checkWorkspaceAccess();
  }

  private handleUserEvents() {
    EmitterService.get(AllowedOperationsUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.authUser = this.authService.getAuthUser();
        this.refreshPermissionFlags();
      });

    EmitterService.get('authUser.updateCompany')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.authUser = this.authService.getAuthUser();
      });
  }

  private handleWorkspacesEvents() {
    EmitterService.get(ActiveWorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (updates) => {
          this.activeWorkspace = updates.workspace;
          this.inMarketplaceWorkspace = updates.inMarketplace;
          this.checkWorkspaceAccess();
        },
      });

    EmitterService.get(ProjectWorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          if (this.projectService.project) {
            this.project = this.projectService.project;
          }
          this.cacheBuster = Date.now();
        },
      });

    EmitterService.get(WorkspaceAvatarUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.cacheBuster = Date.now();
      });

    EmitterService.get(WorkspaceDeleted)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.cacheBuster = Date.now();
      });
  }

  private handleCampaignEvents() {
    this.timelineService.activeCampaign$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((campaign) => {
      this.activeCampaign = campaign;
      this.canEditCampaign = !isCampaignLocked(this.permissionService, this.activeCampaign, this.authUser.username);
      this.adjustActiveWorkspace();
    });
  }

  private handleProjectEvents() {
    onLoadProject.pipe(takeUntil(this.ngUnsubscribe)).subscribe((project) => {
      if (this.projectService.projectOnloaded) {
        if (project.id) {
          this.project = project;
          this.adjustActiveWorkspace();
        } else {
          //TODO: fix leave from project
          this.projectService.projectOnloaded = false;
        }
      } else {
        // Pages like notification-setting emit a basic project before projectOnloaded flips true;
        // keep the menu for a real project, clear it only on empty "left project" emissions.
        this.project = project?.id ? project : null;
      }
    });

    EmitterService.get(ProjectImageUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.projectCacheBuster = Date.now().toString();
      });
  }

  private detectRouteChange() {
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.trackNavigation(event.urlAfterRedirects);
      }
    });
  }

  private trackNavigation(url: string) {
    this.isSidebarOpen = false;

    this.cacheBuster = new Date().getTime().toString();
    let isInsideProject: boolean = false;
    const urlObj = new URL(url, window.location.origin);

    const isWorkspaceTaskRoute = /^\/workspaces\/[^/]+\/tasks/.test(urlObj.pathname);
    const isCampaignTaskRoute = /^\/workspaces\/[^/]+\/campaign-drive\/[^/]+\/tasks/.test(urlObj.pathname);
    const isTaskRoute = isWorkspaceTaskRoute || isCampaignTaskRoute;

    if (urlObj.pathname === '/workspaces') {
      this.isActiveCampaignMenuHide = false;
      this.isActiveWorkspaceMenuHide = false;
    } else {
      this.isActiveCampaignMenuHide = true;
      this.isActiveWorkspaceMenuHide = true;
    }

    if (urlObj.pathname === '/workspaces') {
      this.activeMenuItem = 'workspaces';
    } else if (url.startsWith('/my-space')) {
      this.activeMenuItem = 'my-space';
    } else if (url.startsWith('/workspaces/campaign-drive') && !url.includes('?wid=')) {
      this.activeMenuItem = 'drive-campaign';
    } else if (url.startsWith('/workspaces/analytics')) {
      this.activeMenuItem = 'reporting';
    } else if (url.startsWith('/dashboard')) {
      this.activeMenuItem = 'reporting';
    } else {
      this.activeMenuItem = '';
    }
    if (url.startsWith('/projects')) {
      isInsideProject = true;
    }

    if (!isInsideProject && this.activeMenuItem != 'drive-campaign' && !isTaskRoute) {
      if (!(url.includes('/genral') || url.includes('/recap'))) this.timelineService.activeCampaign$.next(null);
    }

    if (url.startsWith('/workspaces/campaign-drive') && url.includes('?wid=')) {
      if (this.currentWorkspace && !this.activeWorkspace) this.activeWorkspace = this.currentWorkspace;
      this.isActiveCampaignMenuHide = false;
    } else if (urlObj.pathname !== '/workspaces') {
      this.isActiveCampaignMenuHide = true;
    }

    if (urlObj.pathname == '/workspaces/projects' && urlObj.search == '') {
      this.activeWorkspace = null;
    }
  }

  logout() {
    this.authService.logout({ message: 'from logout menu' });
    // Emit event and redirect to Login page
    EmitterService.get('user.logout').emit();
    this.broadcastService.send({ action: 'user.logout' });
    this.router.navigate(['/entry']);
    this.reportingService.clearMenuState();
  }

  toggleMenuState(menuType: SidebarMenuTypes) {
    if (this.menuStates[menuType].isExpanded) {
      this.setMenuState(menuType, false);
    } else {
      this.setMenuState('brand', menuType === 'brand');
      this.setMenuState('workspace', menuType === 'workspace');
      this.setMenuState('campaign', menuType === 'campaign');
      this.setMenuState('project', menuType === 'project');
      this.setMenuState('account', menuType === 'account');
    }
  }

  private setMenuState(menuType: SidebarMenuTypes, isExpanded: boolean) {
    this.menuStates[menuType].isExpanded = isExpanded;
    this.adjustMenuState(menuType);
  }

  private adjustMenuState(menuType: SidebarMenuTypes) {
    this.menuStates[menuType].toggleAlt = this.menuStates[menuType].isExpanded ? 'collapse' : 'expand';
    this.menuStates[menuType].toggleIcon = this.menuStates[menuType].isExpanded
      ? '/assets/svg/sidebar/collapse-arrow.svg'
      : '/assets/svg/sidebar/expand-arrow.svg';
  }

  minimizeSidebar() {
    this.setMenuState('brand', false);
    this.setMenuState('workspace', false);
    this.setMenuState('campaign', false);
    this.setMenuState('project', false);
    this.setMenuState('account', false);
  }

  goToCampaign(event: MouseEvent) {
    event.preventDefault();
    this.projectV2Service.updateSelectedDriveCampaign(this.activeCampaign.workspaceId, this.activeCampaign.id);
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  selectBrand(brand: Brand): void {
    this.brandService.switchBrand(brand);
  }

  private adjustActiveWorkspace() {
    if (!this.project?.workspaceId && !this.activeCampaign?.workspaceId) {
      return;
    }

    if (
      this.activeWorkspace &&
      (this.project?.workspaceId === this.activeWorkspace?.id ||
        this.activeCampaign.workspaceId === this.activeWorkspace?.id)
    ) {
      return;
    }

    const workspaceId = this.project ? this.project.workspaceId : this.activeCampaign.workspaceId;

    //if non ltg
    if (this.workspaces?.length > 0) {
      const recordedWorkspace = this.workspaces.find((w) => w.id == workspaceId);
      if (recordedWorkspace) {
        this.activeWorkspace = recordedWorkspace;
        this.checkWorkspaceAccess();
        const currentWorkspace = this.workspaceService.getActiveWorkspace();
        if (
          !currentWorkspace ||
          currentWorkspace.id != this.project?.workspaceId ||
          currentWorkspace.companyId != this.project.company.id
        )
          this.workspaceService.setActiveWorkspace(this.activeWorkspace);
        this.inMarketplaceWorkspace = false;
        return;
      }
    }

    this.workspaceService
      .getWorkspaceById(this.authUser.companyId, workspaceId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (workspace) => {
          if (workspace) {
            this.workspaceService.setActiveWorkspace(workspace);
          }
        },
        error: (error) => {},
      });
  }

  goToInitialPage() {
    this.project = null;
    this.workspaceService.unsetActiveWorkspace();
    this.router.navigate(['/workspaces']).then(() => {
      this.workspaceService.refreshWorkspaceData();
    });
  }

  goToReportingDashboard() {
    this.router.navigate(['/dashboard']);
  }

  backToCampaign() {
    const workspaceId = this.activeWorkspace.id;
    const campaignId = this.project?.campaignId;
    this.router.navigate(['/workspaces/campaigndrive/dashboard'], {
      queryParams: {
        wid: this.cryptoService.encryptString(workspaceId),
        dc: this.cryptoService.encryptString(campaignId),
      },
    });
  }

  private checkWorkspaceAccess() {
    if (!this.activeWorkspace) {
      return;
    }

    // Workspace settings hosts workspace editing and user management — CORE_COMPANY manage.users
    // or CORE_WORKSPACE update (mirrors canActivateWorkspaceSettings on /workspace/edit/:workspaceId),
    // scoped to the user's own company; admin-console rights only lift the company scoping.
    this.canEditWorkspace =
      (this.permissionService.canManageCompanyUsers() || this.permissionService.canUpdateWorkspace()) &&
      (this.permissionService.isPlatformAdmin() || this.activeWorkspace.companyId === this.authUser.companyId);
  }

  backToWorkspace() {
    const workspaceId = this.activeWorkspace.id;
    this.router.navigate(['/workspaces/campaign-drive'], {
      queryParams: {
        wid: this.cryptoService.encryptString(workspaceId),
      },
    });
  }
}

interface WorkspaceExtraDTO extends WorkspaceDTO {
  canEdit: boolean;
  company: { name: string };
  cacheBuster: number;
}
